import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBars,
  faChartLine,
  faShieldHalved,
  faBolt,
  faDiagramProject,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';

const CATEGORIES: {
  title: string;
  blurb: string;
  to: string;
  icon: IconDefinition;
  accent: string;
}[] = [
  {
    title: 'Monitor',
    blurb: 'Risk scores, PCP status, and situational awareness.',
    to: '/RMT',
    icon: faChartLine,
    accent: '#0f5b57',
  },
  {
    title: 'Prepare',
    blurb: 'Get Prepared resources and emergency toolkits.',
    to: '/get-prepared-wall',
    icon: faShieldHalved,
    accent: '#15736d',
  },
  {
    title: 'Respond',
    blurb: 'Fast Report and emergency response pathways.',
    to: '/fast-report',
    icon: faBolt,
    accent: '#1a8a82',
  },
  {
    title: 'Programme',
    blurb: 'Move FAST progress across key areas and countries.',
    to: '/programme',
    icon: faDiagramProject,
    accent: '#0a4743',
  },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.15 },
  },
};

const flyIn = {
  hidden: { opacity: 0, y: 28, scale: 0.92 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 380, damping: 24 },
  },
};

const NexusGateway: React.FC = () => {
  return (
    <div className="relative overflow-hidden rounded-2xl">
      {/* Atmosphere — soft teal mesh, not flat grey */}
      <div
        className="pointer-events-none absolute inset-0 -z-10"
        aria-hidden
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 10% 0%, rgba(21,115,109,0.18), transparent 55%), radial-gradient(ellipse 70% 50% at 90% 20%, rgba(234,179,8,0.08), transparent 50%), linear-gradient(180deg, #f3f8f7 0%, #eef4f3 45%, #f7f8f9 100%)',
        }}
      />

      {/* Hero */}
      <section className="px-1 pt-2 pb-8 sm:pb-10 sm:px-2">
        <motion.p
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.2em] text-[#15736d]"
        >
          EuFMD digital workspace
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.05 }}
          className="mt-2 font-martaBold text-4xl sm:text-5xl md:text-6xl tracking-tight text-[#0a4743] leading-[1.05]"
        >
          Nexus
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.12 }}
          className="mt-4 max-w-2xl text-base sm:text-lg text-gray-700 leading-relaxed"
        >
          EuFMD&apos;s shared digital workspace — bringing together the applications,
          tools and information resources that support our work worldwide.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-6 flex flex-wrap items-center gap-3"
        >
          <Link
            to="/programme"
            className="inline-flex items-center gap-2 rounded-lg bg-[#15736d] text-white font-semibold text-sm sm:text-base px-5 py-2.5 shadow-sm hover:bg-[#0f5b57] transition-colors"
          >
            View EuFMD&apos;s Programme
            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
          </Link>
          <span className="text-sm text-gray-500">or jump into a category below</span>
        </motion.div>
      </section>

      {/* Category tiles */}
      <section className="px-1 sm:px-2 pb-8">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#0f5b57] mb-3">
          What you can do
        </h2>
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"
        >
          {CATEGORIES.map((cat) => (
            <motion.div key={cat.title} variants={flyIn}>
              <Link
                to={cat.to}
                className="group flex flex-col h-full rounded-xl border border-gray-200/90 bg-white px-4 py-4 shadow-sm hover:border-[#15736d]/50 hover:shadow-md transition-all"
              >
                <span
                  className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm transition-transform group-hover:-translate-y-0.5 group-hover:scale-105"
                  style={{ backgroundColor: cat.accent }}
                >
                  <FontAwesomeIcon icon={cat.icon} className="text-lg" />
                </span>
                <span className="font-martaBold text-lg text-gray-900">{cat.title}</span>
                <span className="mt-1 text-xs text-gray-600 leading-snug flex-1">{cat.blurb}</span>
                <span className="mt-3 text-xs font-semibold text-[#15736d] inline-flex items-center gap-1 opacity-80 group-hover:opacity-100">
                  Open <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* How to navigate */}
      <section className="px-1 sm:px-2 pb-4">
        <h2 className="text-sm font-bold uppercase tracking-wider text-[#0f5b57] mb-3">
          How to find things
        </h2>
        <div className="grid sm:grid-cols-2 gap-3 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.25, duration: 0.4 }}
            className="flex gap-3 items-start rounded-xl border border-[#b7d0cc]/80 bg-white/80 backdrop-blur-sm px-4 py-3.5"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#15736d] text-white">
              <FontAwesomeIcon icon={faBars} />
            </span>
            <div>
              <p className="font-semibold text-gray-900">MENU sidebar</p>
              <p className="text-sm text-gray-600 mt-0.5 leading-snug">
                Tap the three lines at the top left to open the full list of tools and resources.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.32, duration: 0.4 }}
            className="flex gap-3 items-start rounded-xl border border-[#b7d0cc]/80 bg-white/80 backdrop-blur-sm px-4 py-3.5"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full overflow-hidden border-2 border-white shadow bg-[#f4faf9]">
              <img
                src={`${process.env.PUBLIC_URL}/cow.png`}
                alt=""
                className="h-full w-full object-cover"
              />
            </span>
            <div>
              <p className="font-semibold text-gray-900">Cow assistant</p>
              <p className="text-sm text-gray-600 mt-0.5 leading-snug">
                Bottom-right: search pages and tools instantly, or leave feedback.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default NexusGateway;
